import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Grid,
  Tabs,
  Tab,
  Chip,
  FormControlLabel,
  Switch,
  MenuItem,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, RestoreFromTrash as RestoreIcon, FilterListOff as FilterListOffIcon } from '@mui/icons-material';
import { PrimeNonExoneree, PrimeNonExonereeCreate, PrimeNonExonereeUpdate } from '../../../types/paie';
import { primesNonExonereesAPI } from '../../../services/api';
import { getModuleTableTheme } from '../../../theme/tableTheme';
import AlertDialog from '../../../components/AlertDialog';
import useAlert from '../../../hooks/useAlert';
import useFormDraft from '../../../hooks/useFormDraft';
import PermissionButton from '../../../components/PermissionButton';
import ColumnFilter from '../../../components/ColumnFilter';
import { useColumnFilters } from '../../../hooks/useColumnFilters';
import { extractErrorMessage } from '../../../utils/errorHandler';
import { SlideUpTransition, useMinimizable, DialogControls, MinimizedBar } from '../../../components/MinimizableDialog';

const MOIS_OPTIONS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

const HR_COLOR = '#00897B';

const PRIME_FILTER_SCHEMA = {
  nom:       { type: 'text', param: 'nom' },
  condition: { type: 'text', param: 'condition' },
  one_shot:  { type: 'select', param: 'one_shot' },
  prorate:   { type: 'select', param: 'prorate' },
  is_active: { type: 'select', param: 'is_active' },
} as const;

const PERIODE_OPTIONS = [
  { value: 'true', label: 'Ponctuelle' },
  { value: 'false', label: 'Tous les mois' },
];
const PRORATION_OPTIONS = [
  { value: 'true', label: 'Proraté' },
  { value: 'false', label: 'Montant fixe' },
];
const STATUT_OPTIONS = [
  { value: 'true', label: 'Actif' },
  { value: 'false', label: 'Inactif' },
];

const PrimeNonExonereeManagement: React.FC = () => {
  const [primes, setPrimes] = useState<PrimeNonExoneree[]>([]);
  const [deletedPrimes, setDeletedPrimes] = useState<PrimeNonExoneree[]>([]);
  const [open, setOpen] = useState(false);
  const { minimized, setMinimized, minimize, restore, dialogOpen } = useMinimizable(open);
  const [editMode, setEditMode] = useState(false);
  const [editingPrime, setEditingPrime] = useState<PrimeNonExoneree | null>(null);
  const [currentPrime, setCurrentPrime] = useState<Partial<PrimeNonExoneree>>({
    nom: '',
    condition: '',
    is_active: true,
    prorate: true,
    one_shot: false,
    base_jours: false,
    mois: null,
    annee: null,
  });
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const { alertState, showError, showSuccess, showConfirm, hideAlert } = useAlert();
  const { filters, update, reset, hasActive } = useColumnFilters(PRIME_FILTER_SCHEMA);

  const filteredPrimes = useMemo(
    () =>
      primes.filter((p) => {
        if (filters.nom && !p.nom.toLowerCase().includes(filters.nom.toLowerCase())) return false;
        if (filters.condition && !(p.condition || '').toLowerCase().includes(filters.condition.toLowerCase())) return false;
        if (filters.one_shot !== '' && Boolean(p.one_shot) !== (filters.one_shot === 'true')) return false;
        if (filters.prorate !== '' && Boolean(p.prorate) !== (filters.prorate === 'true')) return false;
        if (filters.is_active !== '' && Boolean(p.is_active) !== (filters.is_active === 'true')) return false;
        return true;
      }),
    [primes, filters]
  );

  useEffect(() => {
    fetchPrimes();
  }, []);

  const fetchPrimes = async () => {
    try {
      setLoading(true);
      const data = await primesNonExonereesAPI.getAll();
      setPrimes(data.items);
    } catch (error) {
      showError('Erreur lors du chargement des primes non exonérées');
      console.error('Error fetching primes non exonerees:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDeletedPrimes = async () => {
    try {
      setLoading(true);
      const data = await primesNonExonereesAPI.getDeleted();
      setDeletedPrimes(data.items);
    } catch (error) {
      showError('Erreur lors du chargement des primes supprimées');
      console.error('Error fetching deleted primes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (prime: PrimeNonExoneree) => {
    try {
      setLoading(true);
      await primesNonExonereesAPI.restore(prime.id);
      showSuccess('Prime non exonérée restaurée avec succès');
      fetchDeletedPrimes();
      fetchPrimes();
    } catch (error) {
      showError('Erreur lors de la restauration de la prime');
      console.error('Error restoring prime:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    if (newValue === 1) {
      fetchDeletedPrimes();
    }
  };

  // Cache an in-progress new prime so an accidental backdrop-close doesn't wipe it.
  const { saveDraft, loadDraft, clearDraft } = useFormDraft<{
    currentPrime: Partial<PrimeNonExoneree>;
  }>('prime-non-exoneree');

  const handleOpen = (prime?: PrimeNonExoneree) => {
    restore();
    if (prime) {
      setEditingPrime(prime);
      setCurrentPrime({
        nom: prime.nom,
        condition: prime.condition || '',
        is_active: prime.is_active,
        prorate: prime.prorate,
        one_shot: prime.one_shot ?? false,
        base_jours: prime.base_jours ?? false,
        mois: prime.mois ?? null,
        annee: prime.annee ?? null,
      });
      setEditMode(true);
    } else {
      setEditingPrime(null);
      const draft = loadDraft();
      setCurrentPrime(
        draft?.currentPrime ?? {
          nom: '',
          condition: '',
          is_active: true,
          prorate: true,
          base_jours: false,
        }
      );
      setEditMode(false);
    }
    setOpen(true);
  };

  // X / Annuler = deliberate cancel: wipe inputs and discard any draft.
  const handleClose = () => {
    clearDraft();
    setMinimized(false);
    setOpen(false);
    setEditingPrime(null);
    setCurrentPrime({
      nom: '',
      condition: '',
      is_active: true,
      prorate: true,
    });
    setEditMode(false);
  };

  // Backdrop click / Escape = accidental: keep new-prime work as a draft, then close.
  const handleDialogClose = (
    _event: object,
    reason: 'backdropClick' | 'escapeKeyDown'
  ) => {
    if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') return;
    if (!editMode) {
      saveDraft({ currentPrime });
      setOpen(false);
    } else {
      handleClose();
    }
  };

  const handleSave = async () => {
    if (!currentPrime.nom?.trim()) {
      showError('Le nom est obligatoire');
      return;
    }
    if (currentPrime.one_shot && (!currentPrime.mois || !currentPrime.annee)) {
      showError('Mois et année sont obligatoires pour une prime ponctuelle');
      return;
    }

    try {
      setLoading(true);
      const oneShotPayload = currentPrime.one_shot
        ? { one_shot: true, mois: currentPrime.mois, annee: currentPrime.annee }
        : { one_shot: false, mois: null, annee: null };

      if (editMode && editingPrime) {
        const updateData: PrimeNonExonereeUpdate = {
          nom: currentPrime.nom,
          condition: currentPrime.condition || undefined,
          is_active: currentPrime.is_active,
          prorate: currentPrime.prorate,
          base_jours: currentPrime.base_jours ?? false,
          ...oneShotPayload,
        };
        await primesNonExonereesAPI.update(editingPrime.id, updateData);
        showSuccess('Prime non exonérée modifiée avec succès');
      } else {
        const createData: PrimeNonExonereeCreate = {
          nom: currentPrime.nom!,
          condition: currentPrime.condition || undefined,
          is_active: currentPrime.is_active,
          prorate: currentPrime.prorate,
          base_jours: currentPrime.base_jours ?? false,
          ...oneShotPayload,
        };
        await primesNonExonereesAPI.create(createData);
        showSuccess('Prime non exonérée créée avec succès');
      }
      await fetchPrimes();
      handleClose();
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (prime: PrimeNonExoneree) => {
    showConfirm(
      `Êtes-vous sûr de vouloir supprimer la prime "${prime.nom}" ?`,
      async () => {
        try {
          setLoading(true);
          await primesNonExonereesAPI.delete(prime.id);
          showSuccess('Prime non exonérée supprimée avec succès');
          await fetchPrimes();
        } catch (error) {
          showError('Erreur lors de la suppression de la prime');
          console.error('Error deleting prime:', error);
        } finally {
          setLoading(false);
        }
      }
    );
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">Gestion des Primes Non Exonérées</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {tabValue === 0 && hasActive() && (
            <Button
              size="small"
              startIcon={<FilterListOffIcon />}
              onClick={reset}
              sx={{ color: 'text.secondary' }}
            >
              Effacer les filtres
            </Button>
          )}
          {tabValue === 0 && (
            <PermissionButton
              moduleName="HR"
              operation="add"
              miniModuleName="Primes Non Exonérées"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpen()}
            >
              Nouvelle Prime
            </PermissionButton>
          )}
        </Box>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Primes Actives" />
          <Tab label="Corbeille" />
        </Tabs>
      </Box>

      {tabValue === 0 && (
        <Box sx={getModuleTableTheme('hr')}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Nom
                      <ColumnFilter type="text" value={filters.nom} onChange={(v) => update('nom', v)} label="nom" color={HR_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Condition / Description
                      <ColumnFilter type="text" value={filters.condition} onChange={(v) => update('condition', v)} label="condition" color={HR_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Période
                      <ColumnFilter type="select" value={filters.one_shot} onChange={(v) => update('one_shot', v)} options={PERIODE_OPTIONS} label="période" color={HR_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Proration
                      <ColumnFilter type="select" value={filters.prorate} onChange={(v) => update('prorate', v)} options={PRORATION_OPTIONS} label="proration" color={HR_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Statut
                      <ColumnFilter type="select" value={filters.is_active} onChange={(v) => update('is_active', v)} options={STATUT_OPTIONS} label="statut" color={HR_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPrimes.map((prime) => (
                  <TableRow key={prime.id}>
                    <TableCell>{prime.nom}</TableCell>
                    <TableCell>{prime.condition || '-'}</TableCell>
                    <TableCell>
                      {prime.one_shot && prime.mois && prime.annee ? (
                        <Chip
                          label={`${MOIS_OPTIONS[prime.mois - 1]} ${prime.annee}`}
                          color="warning"
                          size="small"
                          variant="outlined"
                        />
                      ) : (
                        <Chip label="Tous les mois" color="default" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        <Chip
                          label={prime.prorate ? 'Proraté' : 'Montant fixe'}
                          color={prime.prorate ? 'default' : 'info'}
                          size="small"
                          variant="outlined"
                        />
                        {prime.base_jours && (
                          <Chip label="En jours" color="warning" size="small" variant="outlined" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={prime.is_active ? 'Actif' : 'Inactif'}
                        color={prime.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <PermissionButton
                        moduleName="HR"
                        operation="edit"
                        miniModuleName="Primes Non Exonérées"
                        component="IconButton"
                        onClick={() => handleOpen(prime)}
                        color="primary"
                      >
                        <EditIcon />
                      </PermissionButton>
                      <PermissionButton
                        moduleName="HR"
                        operation="delete"
                        miniModuleName="Primes Non Exonérées"
                        component="IconButton"
                        onClick={() => handleDelete(prime)}
                        color="error"
                      >
                        <DeleteIcon />
                      </PermissionButton>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredPrimes.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      {hasActive() ? 'Aucune prime ne correspond aux filtres' : 'Aucune prime non exonérée trouvée'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {tabValue === 1 && (
        <Box sx={getModuleTableTheme('hr')}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nom</TableCell>
                  <TableCell>Condition / Description</TableCell>
                  <TableCell>Date de Suppression</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {deletedPrimes.map((prime) => (
                  <TableRow key={prime.id}>
                    <TableCell>{prime.nom}</TableCell>
                    <TableCell>{prime.condition || '-'}</TableCell>
                    <TableCell>
                      {prime.deleted_at ? new Date(prime.deleted_at).toLocaleDateString('fr-FR') : '-'}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        onClick={() => {
                          showConfirm(
                            `Êtes-vous sûr de vouloir restaurer la prime "${prime.nom}" ?`,
                            () => handleRestore(prime),
                            {
                              title: 'Confirmer la restauration',
                              confirmText: 'Restaurer',
                              cancelText: 'Annuler',
                            }
                          );
                        }}
                        color="primary"
                        title="Restaurer"
                      >
                        <RestoreIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {deletedPrimes.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      Aucune prime supprimée
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth TransitionComponent={SlideUpTransition}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {editMode ? 'Modifier la Prime Non Exonérée' : 'Nouvelle Prime Non Exonérée'}
          <DialogControls color="#009688" onMinimize={minimize} onClose={handleClose} />
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                variant="filled"
                fullWidth
                label="Nom"
                value={currentPrime.nom || ''}
                onChange={(e) => setCurrentPrime({ ...currentPrime, nom: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                variant="filled"
                fullWidth
                label="Condition / Description"
                value={currentPrime.condition || ''}
                onChange={(e) => setCurrentPrime({ ...currentPrime, condition: e.target.value })}
                multiline
                rows={3}
                placeholder="Décrivez les conditions d'attribution de cette prime..."
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={currentPrime.prorate ?? true}
                    onChange={(e) => setCurrentPrime({ ...currentPrime, prorate: e.target.checked })}
                  />
                }
                label={
                  (currentPrime.prorate ?? true)
                    ? 'Prime proratée selon les heures travaillées'
                    : 'Montant fixe (non proraté) — payée en totalité chaque mois où elle s’applique'
                }
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={currentPrime.one_shot ?? false}
                    onChange={(e) => setCurrentPrime({
                      ...currentPrime,
                      one_shot: e.target.checked,
                      mois: e.target.checked ? currentPrime.mois : null,
                      annee: e.target.checked ? currentPrime.annee : null,
                    })}
                  />
                }
                label={
                  currentPrime.one_shot
                    ? 'Prime ponctuelle — payée une seule fois sur le mois ci-dessous'
                    : 'Prime récurrente — appliquée tous les mois'
                }
              />
            </Grid>
            {currentPrime.one_shot && (
              <>
                <Grid item xs={6}>
                  <TextField
                    variant="filled"
                    fullWidth
                    select
                    required
                    label="Mois"
                    value={currentPrime.mois ?? ''}
                    onChange={(e) => setCurrentPrime({ ...currentPrime, mois: Number(e.target.value) })}
                  >
                    {MOIS_OPTIONS.map((m, i) => (
                      <MenuItem key={i + 1} value={i + 1}>{m}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    variant="filled"
                    fullWidth
                    type="number"
                    required
                    label="Année"
                    value={currentPrime.annee ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      setCurrentPrime({ ...currentPrime, annee: v === '' ? null : Number(v) });
                    }}
                    inputProps={{ min: 2020, max: 2099 }}
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={currentPrime.base_jours ?? false}
                    onChange={(e) => setCurrentPrime({ ...currentPrime, base_jours: e.target.checked })}
                  />
                }
                label={
                  currentPrime.base_jours
                    ? 'Basée sur des jours — saisir un nombre de jours, le montant est calculé (jours × salaire de base / 26)'
                    : 'Montant saisi directement'
                }
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={currentPrime.is_active ?? true}
                    onChange={(e) => setCurrentPrime({ ...currentPrime, is_active: e.target.checked })}
                  />
                }
                label="Actif"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Annuler</Button>
          <Button onClick={handleSave} variant="contained" disabled={loading}>
            {editMode ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      <MinimizedBar
        open={open && minimized}
        color="#009688"
        title={editMode ? 'Modifier la Prime Non Exonérée' : 'Nouvelle Prime Non Exonérée'}
        onRestore={restore}
        onClose={handleClose}
      />

      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
      />
    </Box>
  );
};

export default PrimeNonExonereeManagement;
